import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, DollarSign, TrendingUp, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { LEAD_STAGE_LABELS } from "@/lib/constants";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/leads/stats").then((r) => setStats(r.data));
    api.get("/leads").then((r) => setRecent(r.data.slice(0, 6)));
  }, []);

  if (!stats) return <p className="text-[#A1A1AA]">Carregando...</p>;

  const stageData = Object.entries(stats.by_stage).map(([k, v]) => ({ name: LEAD_STAGE_LABELS[k] || k, value: v }));
  const equipData = Object.entries(stats.by_equipment).map(([k, v]) => ({ name: k, value: v })).sort((a, b) => b.value - a.value).slice(0, 6);
  const won = stats.by_stage.GANHO || 0;
  const cards = [
    { icon: Users, label: "Total de Leads", value: stats.total, c: "#FF5500" },
    { icon: TrendingUp, label: "Em Negociação", value: (stats.by_stage.NEGOCIACAO || 0) + (stats.by_stage.PROPOSTA || 0), c: "#007AFF" },
    { icon: Package, label: "Ganhos", value: won, c: "#22c55e" },
    { icon: DollarSign, label: "Valor Potencial", value: `R$ ${stats.total_value.toLocaleString("pt-BR")}`, c: "#eab308" },
  ];

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-white">Dashboard</h1>
      <p className="mt-1 text-sm text-[#A1A1AA]">Visão geral da inteligência comercial.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label}`} className="border border-[#27272A] bg-[#151618] p-5">
            <c.icon className="h-6 w-6" style={{ color: c.c }} />
            <div className="mt-3 font-heading text-2xl font-black text-white">{c.value}</div>
            <div className="mt-1 text-xs uppercase tracking-wider text-[#A1A1AA]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="border border-[#27272A] bg-[#151618] p-5">
          <h3 className="mb-4 font-heading font-bold text-white">Funil por estágio</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stageData}>
              <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fill: "#A1A1AA", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0B", border: "1px solid #27272A", color: "#fff" }} />
              <Bar dataKey="value" fill="#FF5500" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-[#27272A] bg-[#151618] p-5">
          <h3 className="mb-4 font-heading font-bold text-white">Demanda por equipamento</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={equipData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fill: "#A1A1AA", fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 10 }} width={120} />
              <Tooltip contentStyle={{ background: "#0A0A0B", border: "1px solid #27272A", color: "#fff" }} />
              <Bar dataKey="value">{equipData.map((_, i) => <Cell key={i} fill="#007AFF" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 border border-[#27272A] bg-[#151618]">
        <div className="flex items-center justify-between border-b border-[#27272A] px-5 py-3">
          <h3 className="font-heading font-bold text-white">Leads recentes</h3>
          <Link to="/admin/leads" className="text-sm text-[#FF5500] hover:underline">Ver todos</Link>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {recent.map((l) => (
              <tr key={l.id} className="border-b border-[#27272A] last:border-0">
                <td className="px-5 py-3 font-medium text-white">{l.name}</td>
                <td className="px-5 py-3 text-[#A1A1AA]">{l.equipment_type}</td>
                <td className="px-5 py-3 text-[#A1A1AA]">{l.city} {l.state}</td>
                <td className="px-5 py-3 text-[#A1A1AA]">{LEAD_STAGE_LABELS[l.status]}</td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td className="px-5 py-6 text-[#A1A1AA]">Nenhum lead ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
